import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { useTaskManager } from "@/contexts/TaskManager";
import { updateTaskNotes } from "@/lib/taskClient"; // Добавлено
import {
  WindowHeader,
  WindowContent,
  WindowGrid,
  WindowSection,
  WindowSidebar
} from "@/components/ui/window-layout";
import {
  CheckSquare,
  Clock,
  AlertTriangle,
  Target,
  User,
  Calendar,
  FileText,
  MessageSquare,
  Check,
  Play,
  Pause,
  RotateCcw,
  Tag
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {useClerk} from "@clerk/clerk-react";

interface Task {
  id: string;
  title: string;
  description: string;
  priority: "high" | "medium" | "low";
  dueTime: string;
  category: string;
  assignee?: string;
  progress: number;
  details: string;
  notes?: string;
}

interface TaskDetailsWindowProps {
  task: Task;
}

export default function TaskDetailsWindow({ task }: TaskDetailsWindowProps) {
  const [progress, setProgress] = useState(task.progress);
  const [notes, setNotes] = useState(task.notes || "");
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [hasNotesChanges, setHasNotesChanges] = useState(false);
  const [isInProgress, setIsInProgress] = useState(task.progress > 0 && task.progress < 100);
  const { toast } = useToast();
  const { updateTask } = useTaskManager();

  const clerkUserId = useClerk().user.id

  useEffect(() => {
    setNotes(task.notes || "");
  }, [task.notes]);

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case "high": return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case "medium": return <Clock className="w-5 h-5 text-amber-500" />;
      case "low": return <CheckSquare className="w-5 h-5 text-green-500" />;
      default: return <Target className="w-5 h-5 text-blue-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "border-red-500/30 text-red-400 bg-red-500/10";
      case "medium": return "border-amber-500/30 text-amber-400 bg-amber-500/10";
      case "low": return "border-green-500/30 text-green-400 bg-green-500/10";
      default: return "border-blue-500/30 text-blue-400 bg-blue-500/10";
    }
  };

  const handleStartTask = () => {
    setIsInProgress(true);
    const newProgress = progress === 0 ? 10 : progress;
    setProgress(newProgress);
    updateTask(task.id, { progress: newProgress });
    toast({
      title: "Task Started",
      description: `Started working on "${task.title}"`
    });
  };

  const handlePauseTask = () => {
    setIsInProgress(false);
    updateTask(task.id, { progress });
    toast({
      title: "Task Paused",
      description: `Paused work on "${task.title}"`
    });
  };

  const handleCompleteTask = () => {
    setProgress(100);
    setIsInProgress(false);
    updateTask(task.id, { progress: 100 });
    toast({
      title: "Task Completed!",
      description: `"${task.title}" has been marked as complete`
    });
  };

  const handleUpdateProgress = (value: number) => {
    setProgress(value);
    updateTask(task.id, { progress: value });
    if (value === 100) {
      setIsInProgress(false);
      toast({
        title: "Task Completed!",
        description: `"${task.title}" has been marked as complete`
      });
    }
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
    setHasNotesChanges(true);
  };

  const handleSaveNotes = async () => {
    try {
      setIsSavingNotes(true);

      await updateTaskNotes(task.id, notes, clerkUserId);

      updateTask(task.id, { notes });

      setHasNotesChanges(false);

      toast({
        title: "Notes Saved",
        description: "Your notes have been saved successfully"
      });
    } catch (error) {
      console.error('Error saving notes:', error);
      toast({
        title: "Error Saving Notes",
        description: error instanceof Error ? error.message : "Failed to save notes",
        variant: "destructive"
      });
    } finally {
      setIsSavingNotes(false);
    }
  };

  return (
      <div className="h-full flex flex-col bg-transparent">
        <WindowHeader
            title={task.title}
            subtitle={`${task.category} • Due ${task.dueTime}`}
            icon={getPriorityIcon(task.priority)}
        />

        <WindowContent>
          <WindowGrid cols={3}>
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Task Overview */}
              <WindowSection title="Task Overview" icon={<FileText className="w-5 h-5" />} variant="strong">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Description</h4>
                    <p className="text-muted-foreground leading-relaxed">{task.description}</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-foreground mb-2">Details</h4>
                    <p className="text-muted-foreground leading-relaxed text-sm">{task.details}</p>
                  </div>
                </div>
              </WindowSection>

              {/* Progress Section */}
              <WindowSection title="Progress" icon={<Target className="w-5 h-5" />} variant="strong">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-medium text-foreground">Completion</span>
                      <span className="text-sm font-semibold text-foreground">{progress}%</span>
                    </div>
                    <Progress
                        value={progress}
                        className="h-3"
                    />
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {progress < 100 && !isInProgress && (
                        <Button onClick={handleStartTask} size="sm" className="flex items-center gap-2">
                          <Play className="w-4 h-4" />
                          Start Task
                        </Button>
                    )}

                    {isInProgress && (
                        <Button onClick={handlePauseTask} variant="outline" size="sm" className="flex items-center gap-2">
                          <Pause className="w-4 h-4" />
                          Pause
                        </Button>
                    )}

                    {progress < 100 && (
                        <Button onClick={handleCompleteTask} variant="outline" size="sm" className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Mark Complete
                        </Button>
                    )}

                    {progress > 0 && (
                        <Button
                            onClick={() => handleUpdateProgress(0)}
                            variant="ghost"
                            size="sm"
                            className="flex items-center gap-2"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Reset
                        </Button>
                    )}
                  </div>
                </div>
              </WindowSection>

              {/* Notes Section - ОБНОВЛЕНО */}
              <WindowSection title="Notes" icon={<MessageSquare className="w-5 h-5" />} variant="default">
                <div className="space-y-3">
                  <Textarea
                      placeholder="Add your notes about this task..."
                      value={notes}
                      onChange={handleNotesChange}
                      className="bg-white/5 border-white/10 min-h-24"
                      disabled={isSavingNotes}
                  />
                  <div className="flex items-center justify-between">
                    <Button
                        size="sm"
                        onClick={handleSaveNotes}
                        disabled={!hasNotesChanges || isSavingNotes}
                    >
                      {isSavingNotes ? 'Saving...' : 'Save Notes'}
                    </Button>
                    {hasNotesChanges && !isSavingNotes && (
                        <span className="text-xs text-amber-400">You have unsaved changes</span>
                    )}
                  </div>
                </div>
              </WindowSection>
            </div>

            {/* Sidebar */}
            <WindowSidebar>
              {/* Task Info */}
              <WindowSection title="Task Info" icon={<Tag className="w-5 h-5" />}>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Priority</span>
                    <Badge className={cn("capitalize text-xs", getPriorityColor(task.priority))}>
                      {task.priority}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Category</span>
                    <Badge variant="outline" className="text-xs bg-white/5 border-white/10">
                      {task.category}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Due Time</span>
                    <span className="text-sm font-medium text-foreground">{task.dueTime}</span>
                  </div>

                  {task.assignee && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Assignee</span>
                        <div className="flex items-center gap-2">
                          <User className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm font-medium text-foreground">{task.assignee}</span>
                        </div>
                      </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <Badge variant="outline" className={cn(
                        "text-xs",
                        progress === 100 ? "border-green-500/30 text-green-400 bg-green-500/10" :
                            isInProgress ? "border-blue-500/30 text-blue-400 bg-blue-500/10" :
                                "border-gray-500/30 text-gray-400 bg-gray-500/10"
                    )}>
                      {progress === 100 ? "Completed" : isInProgress ? "In Progress" : "Pending"}
                    </Badge>
                  </div>
                </div>
              </WindowSection>

              {/* Quick Actions */}
              <WindowSection title="Quick Actions">
                <div className="space-y-2">
                  {progress < 25 && (
                      <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => handleUpdateProgress(25)}
                      >
                        Mark 25% Complete
                      </Button>
                  )}
                  {progress < 50 && progress >= 25 && (
                      <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => handleUpdateProgress(50)}
                      >
                        Mark 50% Complete
                      </Button>
                  )}
                  {progress < 75 && progress >= 50 && (
                      <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => handleUpdateProgress(75)}
                      >
                        Mark 75% Complete
                      </Button>
                  )}
                  <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => handleUpdateProgress(100)}
                      disabled={progress === 100}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Complete Task
                  </Button>
                </div>
              </WindowSection>
            </WindowSidebar>
          </WindowGrid>
        </WindowContent>
      </div>
  );
}